import React, { createContext, useContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { db } from '../../../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';

import {
  setPresenceByUserId,
  setTypingByConversation,
} from '../../../redux/slices/chatSlice';


/**
 * User Presence Context
 * Handles online status, typing indicators, and user heartbeats
 */
const UserPresenceContext = createContext(null);

export { UserPresenceContext };

export const usePresence = () => {
  const ctx = useContext(UserPresenceContext);
  if (!ctx) {
    throw new Error('usePresence must be used within UserPresenceProvider');
  }
  return ctx;
};

export const UserPresenceProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Redux Selectors
  const currentUser = useSelector(state => state.auth.currentUser);
  const userData = useSelector(state => state.auth.userData);
  const conversations = useSelector(state => state.chat.conversations);
  const activeConversationId = useSelector(state => state.chat.activeConversationId);
  const presenceByUserId = useSelector(state => state.chat.presenceByUserId);
  const typingByConversation = useSelector(state => state.chat.typingByConversation);

  // Refs
  // FIX 1.2: Generation counter to invalidate stale chunked query callbacks
  const presenceGenRef = useRef(0);

  // Set user presence (heartbeat)
  useEffect(() => {
    if (!currentUser?.uid) {
      dispatch(setPresenceByUserId({}));
      return undefined;
    }

    // FIX 3.1: Use local variables instead of returning cleanup from async function
    let isMounted = true;
    let heartbeatInterval = null;
    let handleVisibilityChange = null;
    let handleBeforeUnload = null;
    let userInfo = userData;

    const updatePresence = async () => {
      if (!userInfo) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists() && isMounted) {
            userInfo = userDocSnap.data();
          }
        } catch (error) {
          console.error('[Presence] Failed to fetch userData:', error);
        }
      }

      if (!isMounted || !userInfo) return;

      const presenceDocRef = doc(db, 'user_presence', currentUser.uid);
      const displayName = userInfo.displayName || userInfo.username || userInfo.name || '';
      const username = userInfo.username || userInfo.displayName || '';
      const photoURL = userInfo.photoURL || '';

      // FIX 2.3: Presence update with retry on failure
      const updatePresenceData = async (isOnline, retries = 3) => {
        for (let i = 0; i < retries; i++) {
          try {
            await setDoc(presenceDocRef, {
              userId: currentUser.uid,
              displayName,
              username,
              photoURL,
              isOnline,
              lastActiveAt: serverTimestamp(),
            }, { merge: true });
            return; // Success
          } catch (error) {
            console.error(`[Presence] write error (attempt ${i + 1}):`, error);
            if (i < retries - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
          }
        }
      };

      // Set online immediately
      updatePresenceData(true);

      // Heartbeat every 30 seconds
      heartbeatInterval = setInterval(() => {
        if (!document.hidden) {
          updatePresenceData(true);
        }
      }, 30 * 1000);

      // Visibility change listener
      handleVisibilityChange = () => {
        updatePresenceData(!document.hidden);
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // FIX H-7: Use synchronous setDoc instead of sendBeacon to nonexistent endpoint
      handleBeforeUnload = () => {
        try {
          const presenceDocRef = doc(db, 'user_presence', currentUser.uid);
          setDoc(presenceDocRef, {
            isOnline: false,
            lastActiveAt: serverTimestamp(),
          }, { merge: true });
        } catch (err) {
          // Best-effort during unload
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
    };

    updatePresence();

    // FIX 3.1: Outer cleanup properly removes all event listeners and intervals
    return () => {
      isMounted = false;
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (handleVisibilityChange) document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (handleBeforeUnload) window.removeEventListener('beforeunload', handleBeforeUnload);

      const presenceDocRef = doc(db, 'user_presence', currentUser.uid);
      try {
        setDoc(presenceDocRef, {
          isOnline: false,
          lastActiveAt: serverTimestamp(),
        }, { merge: true });
      } catch (error) {
        console.error('[Presence] unmount error', error);
      }
    };
    // FIX 5.2: Simplified dependency — userData alone is sufficient since it comes from Redux
  }, [currentUser?.uid, userData, dispatch]);

  // FIX H-1: Memoize member IDs into a stable key to prevent re-subscription
  // on every conversation update (lastMessageAt changes, etc.)
  const memberIdsKey = useMemo(() => {
    const ids = new Set();
    if (currentUser?.uid) ids.add(currentUser.uid);
    conversations?.forEach(conv => {
      if (Array.isArray(conv.memberIds)) {
        conv.memberIds.forEach(id => ids.add(id));
      }
    });
    return [...ids].sort().join(',');
  }, [conversations, currentUser?.uid]);

  // Subscribe to presence for users in active conversations (chunked queries)
  useEffect(() => {
    if (!currentUser?.uid || !memberIdsKey) return undefined;

    const memberIdArray = memberIdsKey.split(',').filter(Boolean);
    const MAX_IN_CLAUSE = 10;

    let unsubscribes = [];

    if (memberIdArray.length <= MAX_IN_CLAUSE) {
      const presenceColRef = collection(db, 'user_presence');
      const q = query(presenceColRef, where('userId', 'in', memberIdArray));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const map = {};
        snapshot.docs.forEach((docSnap) => {
          map[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        dispatch(setPresenceByUserId(map));
      }, (error) => {
        console.error('[Presence] listener error', error);
      });

      unsubscribes.push(unsubscribe);
    } else {
      // Chunked queries for large member sets
      // FIX 1.2: Generation counter invalidates stale chunk callbacks
      // FIX C5: Dispatch incrementally per-chunk instead of waiting for all chunks
      // to prevent dropping updates from individual chunk re-fires
      const generation = ++presenceGenRef.current;
      const presenceMap = {};
      const chunks = [];

      for (let i = 0; i < memberIdArray.length; i += MAX_IN_CLAUSE) {
        chunks.push(memberIdArray.slice(i, i + MAX_IN_CLAUSE));
      }

      chunks.forEach((chunk) => {
        const presenceColRef = collection(db, 'user_presence');
        const q = query(presenceColRef, where('userId', 'in', chunk));

        const unsubscribe = onSnapshot(q, (snapshot) => {
          if (generation !== presenceGenRef.current) return; // Stale generation
          snapshot.docs.forEach((docSnap) => {
            presenceMap[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
          });
          // FIX C5: Dispatch immediately on each chunk update instead of waiting
          // for all chunks. This prevents dropping updates from subsequent re-fires.
          dispatch(setPresenceByUserId({ ...presenceMap }));
        }, (error) => {
          console.error('[Presence] chunk error', error);
        });

        unsubscribes.push(unsubscribe);
      });
    }

    return () => {
      unsubscribes.forEach(unsub => unsub && unsub());
    };
  }, [currentUser?.uid, memberIdsKey, dispatch]);

  // FIX M-14: Subscribe to typing states for active conversation
  useEffect(() => {
    if (!currentUser?.uid || !activeConversationId) return undefined;

    const typingRef = collection(db, 'typing_states');
    const q = query(typingRef, where('conversationId', '==', activeConversationId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const typingUsers = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        // Only include users who are actively typing and not the current user
        if (data.isTyping && data.userId !== currentUser.uid) {
          typingUsers[data.userId] = {
            userId: data.userId,
            isTyping: true,
            updatedAt: data.updatedAt,
          };
        }
      });
      dispatch(setTypingByConversation({
        conversationId: activeConversationId,
        typingUsers,
      }));
    }, (error) => {
      console.error('[Presence] typing listener error', error);
    });

    return () => unsubscribe();
  }, [currentUser?.uid, activeConversationId, dispatch]);

  // Notify typing status
  const notifyTyping = useCallback(async (conversationId, isTyping) => {
    if (!currentUser?.uid || !conversationId) return;
    const typingRef = doc(db, 'typing_states', `${conversationId}_${currentUser.uid}`);
    try {
      const expiresAt = new Date(Date.now() + 5000);
      await setDoc(typingRef, {
        conversationId,
        userId: currentUser.uid,
        isTyping: !!isTyping,
        updatedAt: serverTimestamp(),
        expiresAt,
      }, { merge: true });
    } catch (error) {
      console.error('[Presence] notifyTyping error', error);
    }
  }, [currentUser?.uid]);

  // FIX D6: Wrap context helper closures in useCallback to prevent re-creation
  // ponytail: simple one-liners, native React feature, prevents unnecessary re-renders
  const isUserOnline = useCallback(
    (userId) => presenceByUserId?.[userId]?.isOnline || false,
    [presenceByUserId]
  );
  const getUserPresence = useCallback(
    (userId) => presenceByUserId?.[userId] || null,
    [presenceByUserId]
  );
  const getTypingUsers = useCallback(
    (conversationId) => typingByConversation?.[conversationId] || {},
    [typingByConversation]
  );

  const value = useMemo(() => ({
    // State
    presenceByUserId,
    typingByConversation,

    // Actions
    notifyTyping,

    // Helpers
    isUserOnline,
    getUserPresence,
    getTypingUsers,
  }), [
    presenceByUserId,
    typingByConversation,
    notifyTyping,
    isUserOnline,
    getUserPresence,
    getTypingUsers,
  ]);

  return (
    <UserPresenceContext.Provider value={value}>
      {children}
    </UserPresenceContext.Provider>
  );
};

export default UserPresenceProvider;
